import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist/legacy/build/pdf'

import {
  CertificateField,
  CertificateFieldKey,
  CertificateFontKey,
  CertificateLayout
} from '../../../../services/Event/types'

/**
 * Peças do editor de certificado que não são React: as fontes, a 1ª página do PDF e a medição dos
 * textos.
 *
 * O editor desenha os textos num `<canvas>`, e não em `<div>`s, por um motivo só: o canvas escreve
 * na **linha de base** (`textBaseline = 'alphabetic'`), que é o que a API faz com o FPDF. Um `<div>`
 * posiciona pela caixa da linha, e a distância da caixa até a linha de base muda de fonte para fonte
 * — a prévia ficava alguns pontos acima ou abaixo do PDF. A largura também é medida sem kerning,
 * como o FPDF mede, para o texto encolher no mesmo tamanho nos dois.
 */

export const CERTIFICATE_FIELDS: CertificateFieldKey[] = ['name']

/** Menor fonte a que um texto é reduzido. Espelha `EventCertificateService::MIN_FONT_SIZE`. */
export const MIN_FONT_SIZE = 4

export const CERTIFICATE_FIELD_LABELS: Record<CertificateFieldKey, string> = {
  name: 'Nome do participante'
}

/** As fontes da API (`EventCertificateService::FONTS`), com os mesmos TTFs em `public/fonts/certificates`. */
export const CERTIFICATE_FONTS: Record<CertificateFontKey, { label: string; file: string }> = {
  'georama-regular': { label: 'Georama', file: 'Georama-Regular.ttf' },
  'georama-bold': { label: 'Georama Bold', file: 'Georama-Bold.ttf' },
  'montserrat-light': { label: 'Montserrat Light', file: 'Montserrat-Light.ttf' },
  'montserrat-regular': { label: 'Montserrat', file: 'Montserrat-Regular.ttf' },
  'montserrat-medium': { label: 'Montserrat Medium', file: 'Montserrat-Medium.ttf' },
  'montserrat-bold': { label: 'Montserrat Bold', file: 'Montserrat-Bold.ttf' },
  'opensans-regular': { label: 'Open Sans', file: 'OpenSans-Regular.ttf' },
  'opensans-semibold': { label: 'Open Sans SemiBold', file: 'OpenSans-SemiBold.ttf' },
  'opensans-bold': { label: 'Open Sans Bold', file: 'OpenSans-Bold.ttf' }
}

/** Uma família por fonte, com nome próprio, para não brigar com a Montserrat que o painel já carrega. */
const fontFamily = (font: CertificateFontKey): string => `abra-certificate-${font}`

let fontsPromise: Promise<void> | null = null

/** Carrega os TTFs do certificado uma vez por sessão. Uma fonte que falha cai na fonte padrão do canvas. */
export const loadCertificateFonts = (): Promise<void> => {
  if (!fontsPromise) {
    const faces = (Object.keys(CERTIFICATE_FONTS) as CertificateFontKey[]).map((font) => {
      const face = new FontFace(
        fontFamily(font),
        `url(${process.env.PUBLIC_URL}/fonts/certificates/${CERTIFICATE_FONTS[font].file})`
      )
      document.fonts.add(face)

      return face.load()
    })

    fontsPromise = Promise.allSettled(faces).then(() => undefined)
  }

  return fontsPromise
}

/* ─── PDF ─────────────────────────────────────────────── */

let pdfjsPromise: Promise<typeof import('pdfjs-dist/legacy/build/pdf')> | null = null

// Carregado sob demanda: o pdf.js só entra no bundle de quem abre o editor.
const loadPdfjs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/legacy/build/pdf.worker.min.js',
        import.meta.url
      ).toString()

      return pdfjs
    })
  }

  return pdfjsPromise
}

export interface IPdfFirstPage {
  /** Tamanho da página em pt — o mesmo que a API grava e usa para escrever os textos. */
  width: number
  height: number
  pageCount: number
  /** Desenha a página no canvas com `cssWidth` px de largura, na densidade da tela. */
  render: (canvas: HTMLCanvasElement, cssWidth: number) => Promise<void>
  destroy: () => void
}

/** Abre um PDF e prepara a 1ª página — a única que o certificado usa. */
export const openPdfFirstPage = async (data: ArrayBuffer): Promise<IPdfFirstPage> => {
  const pdfjs = await loadPdfjs()
  // O pdf.js transfere o buffer para o worker, e o original fica vazio: vai uma cópia.
  const pdfDocument: PDFDocumentProxy = await pdfjs.getDocument({ data: data.slice(0) }).promise
  const page: PDFPageProxy = await pdfDocument.getPage(1)
  const base = page.getViewport({ scale: 1 })
  let task: RenderTask | null = null

  return {
    width: base.width,
    height: base.height,
    pageCount: pdfDocument.numPages,
    render: async (canvas, cssWidth) => {
      task?.cancel()

      const viewport = page.getViewport({ scale: (cssWidth / base.width) * (window.devicePixelRatio || 1) })
      const context = canvas.getContext('2d')
      if (!context) return

      canvas.width = Math.round(viewport.width)
      canvas.height = Math.round(viewport.height)
      task = page.render({ canvasContext: context, viewport })

      try {
        await task.promise
      } catch (error) {
        // Um redesenho novo (a janela mudou de tamanho) cancela o anterior: não é erro.
        if ((error as { name?: string })?.name !== 'RenderingCancelledException') throw error
      }
    },
    destroy: () => {
      task?.cancel()
      void pdfDocument.destroy()
    }
  }
}

/* ─── Texto ───────────────────────────────────────────── */

export interface IMeasuredField {
  text: string
  /** Tamanho final da fonte, já reduzido para caber em `max_width`. */
  size: number
  width: number
  /** Borda esquerda do texto e da faixa de `max_width`, em pt. */
  left: number
  rangeLeft: number
  ascent: number
  descent: number
}

type TKerningContext = CanvasRenderingContext2D & { fontKerning?: string }

let measureContext: TKerningContext | null = null

const setFont = (context: TKerningContext, field: CertificateField, size: number) => {
  context.font = `${size}px "${fontFamily(field.font)}"`
  // O FPDF não aplica kerning; o canvas aplica por padrão.
  if ('fontKerning' in context) context.fontKerning = 'none'
}

/** O texto como a API escreve: espaços colapsados e, se pedido, em maiúsculas. */
export const fieldText = (field: CertificateField, text: string): string => {
  const collapsed = text.trim().replace(/\s+/g, ' ')

  return field.uppercase ? collapsed.toLocaleUpperCase('pt-BR') : collapsed
}

/** Mede um texto do layout como a API o escreveria. As unidades são pt (1 unidade do canvas = 1 pt). */
export const measureField = (field: CertificateField, text: string): IMeasuredField => {
  if (!measureContext) {
    measureContext = document.createElement('canvas').getContext('2d') as TKerningContext
  }

  const context = measureContext
  const value = fieldText(field, text)
  let size = field.size

  setFont(context, field, size)
  let width = context.measureText(value).width

  if (width > field.max_width) {
    // Mesmo arredondamento e mesmo piso de `EventCertificateService::writeField()`.
    size = Math.max(Math.floor(((size * field.max_width) / width) * 10) / 10, MIN_FONT_SIZE)
    setFont(context, field, size)
    width = context.measureText(value).width
  }

  const metrics = context.measureText(value || 'Hg')
  const ascent = metrics.fontBoundingBoxAscent ?? size * 0.95
  const descent = metrics.fontBoundingBoxDescent ?? size * 0.25

  const anchorShift = (span: number) => (field.align === 'center' ? span / 2 : field.align === 'right' ? span : 0)

  return {
    text: value,
    size,
    width,
    left: field.x - anchorShift(width),
    rangeLeft: field.x - anchorShift(field.max_width),
    ascent,
    descent
  }
}

/** Escreve os textos no canvas por cima da página, na mesma escala dela. */
export const drawFields = (
  canvas: HTMLCanvasElement,
  cssWidth: number,
  page: { width: number; height: number },
  layout: CertificateLayout,
  measured: Record<CertificateFieldKey, IMeasuredField>
): void => {
  const context = canvas.getContext('2d') as TKerningContext | null
  if (!context) return

  const ratio = window.devicePixelRatio || 1
  const scale = (cssWidth / page.width) * ratio

  // A resolução segue `cssWidth`; o CSS estica o canvas até o tamanho real da página na tela.
  canvas.width = Math.round(cssWidth * ratio)
  canvas.height = Math.round(((cssWidth * page.height) / page.width) * ratio)
  context.setTransform(scale, 0, 0, scale, 0, 0)
  context.textBaseline = 'alphabetic'

  CERTIFICATE_FIELDS.forEach((key) => {
    const field = layout[key]
    const { text, size, left } = measured[key]
    if (!field.enabled || !text) return

    setFont(context, field, size)
    context.fillStyle = field.color
    context.fillText(text, left, field.y)
  })
}

/**
 * O layout padrão (medido no tamanho dos certificados do Canva) levado para uma página de outro
 * tamanho — a mesma conta de `EventCertificateService::defaultLayout()`.
 */
export const scaleLayout = (
  layout: CertificateLayout,
  from: { width: number; height: number },
  to: { width: number; height: number }
): CertificateLayout => {
  const sx = to.width / from.width
  const sy = to.height / from.height
  const s = Math.min(sx, sy)
  const round = (value: number, digits: number) => Math.round(value * 10 ** digits) / 10 ** digits

  return CERTIFICATE_FIELDS.reduce((scaled, key) => {
    const field = layout[key]
    scaled[key] = {
      ...field,
      x: round(field.x * sx, 2),
      y: round(field.y * sy, 2),
      max_width: round(field.max_width * sx, 2),
      size: round(field.size * s, 1)
    }

    return scaled
  }, {} as CertificateLayout)
}
